import { memo, useEffect, useRef, useState } from 'react';

import { Box, Icon, IconButton, Menu, Tooltip } from '@zillow/constellation-10';
import { IconHouseOutline, IconLiveBroadcastOutline, IconPlusFilled } from '@zillow/constellation-icons';

import AdaptiveAnchor from '../AdaptiveAnchor';
import { HeaderTitles } from '../../constants/index';
import HeaderNavigationClickstreamEvents from '../../utils/clickstream';

import type { User } from '../../types';

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Link?: React.ComponentType<React.PropsWithChildren<any>>;
  canAccessAddFeedListingsModal: boolean;
  escapeAllLinks?: boolean;
  handleNoFeedIconPlusClick?: (pageClickingTo: string) => void | null;
  onClickPropertyMenuItem?: (event: React.SyntheticEvent) => void | null;
  onClose?: () => void | null;
  onOpen?: () => void | null;
  propertiesPage: string;
  useAbsolutePaths?: boolean;
  userModel: User;
};

const PlusIconPropertyMenu = ({
  Link,
  canAccessAddFeedListingsModal,
  escapeAllLinks = false,
  handleNoFeedIconPlusClick = () => {},
  onClickPropertyMenuItem,
  onClose = () => {},
  onOpen = () => {},
  propertiesPage,
  useAbsolutePaths = false,
  userModel = {} as User,
}: Props) => {
  const [isPropertiesDropdownOpen, setIsPropertiesDropdownOpen] = useState<boolean>(false);

  const didMountRef = useRef(false);
  // onOpen/onClose should only fire when isPropertiesDropdownOpen actually changes, not when the
  // caller passes a new callback identity - read the latest via ref instead of depending on them.
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
    } else {
      // run the effect for subsequent renders
      if (isPropertiesDropdownOpen) {
        if (onOpenRef.current) {
          onOpenRef.current();
        }
      } else {
        if (onCloseRef.current) {
          onCloseRef.current();
        }
      }
    }
  }, [isPropertiesDropdownOpen]);

  const appendQueryParam = ({ key, value }: { key: string; value: string }) => {
    // window.location isn't available during SSR
    const queryParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : undefined);
    queryParams.set(key, value);

    return queryParams.toString();
  };

  const handleClickPropertyMenuItem = (event: React.SyntheticEvent) => {
    if (onClickPropertyMenuItem) {
      onClickPropertyMenuItem(event);
    }
  };

  const emitCSEventOnAddPropertyClick = () => {
    HeaderNavigationClickstreamEvents.ClickAddPropertyFromDropdownMenu({ user: userModel });
  };

  const emitCSEventOnAddFeedClick = () => {
    HeaderNavigationClickstreamEvents.ClickAddFeedFromDropdownMenu({ user: userModel });
  };

  const navigateToPath = (queryKey: string) => {
    const targetPath = `${propertiesPage}?${appendQueryParam({ key: queryKey, value: 'true' })}`;

    if (
      typeof window !== 'undefined' &&
      typeof window.browserHistory !== 'undefined' &&
      escapeAllLinks === false &&
      useAbsolutePaths === false
    ) {
      window.browserHistory.push(targetPath);
    } else {
      window?.open(targetPath, '_self');
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent, queryKey: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();

      setIsPropertiesDropdownOpen(false);
      navigateToPath(queryKey);
    }
  };

  return (
    <Box data-testid="PlusIconPropertyMenu" css={{ marginLeft: 'auto', display: 'flex' }}>
      {canAccessAddFeedListingsModal ? (
        <Menu.Root
          open={isPropertiesDropdownOpen}
          onOpenChange={(open: boolean) => {
            if (open) {
              setIsPropertiesDropdownOpen(true);
              HeaderNavigationClickstreamEvents.OnPropertiesDropdownOpen({ user: userModel });
            } else {
              setIsPropertiesDropdownOpen(false);
            }
          }}
          offset={{ mainAxis: 20, crossAxis: -240 }}
          placement="bottom"
        >
          <Menu.Trigger>
            <IconButton
              title="Add a feed"
              emphasis="bare"
              tone="brand"
              icon={<IconPlusFilled />}
              onClick={(event: React.SyntheticEvent) => {
                // prevent navigation
                event.preventDefault();
                event.stopPropagation();
              }}
              size="sm"
              data-eventid={HeaderNavigationClickstreamEvents.OnPropertiesDropdownOpen.data_event_id}
              css={{ backgroundColor: 'transparent', width: '400', height: '400', padding: '0', overflow: 'hidden' }}
            />
          </Menu.Trigger>
          <Menu.Portal>
            {/* oxlint-disable-next-line constellation/no-raw-spacing-values */}
            <Menu.Content css={{ width: '278px', maxWidth: '278px', borderRadius: '300' }}>
              <AdaptiveAnchor
                title={'Add a property'}
                path={`${propertiesPage}?${appendQueryParam({ key: 'postingPath', value: 'true' })}`}
                link={Link}
                escape={escapeAllLinks}
                useAbsolutePaths={useAbsolutePaths}
              >
                <Menu.Item
                  onClick={(event: React.SyntheticEvent) => {
                    handleClickPropertyMenuItem(event);
                    emitCSEventOnAddPropertyClick();
                  }}
                  onKeyDown={(event: React.KeyboardEvent) => handleMenuKeyDown(event, 'postingPath')}
                  data-eventid={HeaderNavigationClickstreamEvents.ClickAddPropertyFromDropdownMenu.data_event_id}
                >
                  <Menu.ItemIcon>
                    <Icon render={<IconHouseOutline />} />
                  </Menu.ItemIcon>
                  <Menu.ItemLabel>Add a property</Menu.ItemLabel>
                </Menu.Item>
              </AdaptiveAnchor>

              <AdaptiveAnchor
                title={'Add a feed'}
                path={`${propertiesPage}?${appendQueryParam({ key: 'addAFeed', value: 'true' })}`}
                link={Link}
                escape={escapeAllLinks}
                useAbsolutePaths={useAbsolutePaths}
              >
                <Menu.Item
                  onClick={(event: React.SyntheticEvent) => {
                    handleClickPropertyMenuItem(event);
                    emitCSEventOnAddFeedClick();
                  }}
                  onKeyDown={(event: React.KeyboardEvent) => handleMenuKeyDown(event, 'addAFeed')}
                  data-eventid={HeaderNavigationClickstreamEvents.ClickAddFeedFromDropdownMenu.data_event_id}
                >
                  <Menu.ItemIcon>
                    <Icon render={<IconLiveBroadcastOutline />} />
                  </Menu.ItemIcon>
                  <Menu.ItemLabel>Add a feed</Menu.ItemLabel>
                </Menu.Item>
              </AdaptiveAnchor>
            </Menu.Content>
          </Menu.Portal>
        </Menu.Root>
      ) : (
        <Tooltip
          content="Add a property"
          useHoverProps={{ delay: 300 }}
          trigger={
            <IconButton
              title="Add a property"
              emphasis="bare"
              tone="brand"
              icon={<IconPlusFilled />}
              onClick={(event: React.SyntheticEvent) => {
                // prevent navigation
                event.preventDefault();
                event.stopPropagation();

                handleNoFeedIconPlusClick(HeaderTitles.myProperties);
                navigateToPath('postingPath');
              }}
              size="sm"
              css={{ backgroundColor: 'transparent', width: '400', height: '400', padding: '0', overflow: 'hidden' }}
            />
          }
        />
      )}
    </Box>
  );
};

export default memo(PlusIconPropertyMenu);
